import dayjs from "dayjs";
import * as XLSX from "xlsx";

// Resumen de antigüedad de Cuentas por Pagar — one sheet, one row per proveedor with the same
// aging buckets shown on screen. The line-level "detalle de documentos" export reuses
// exportSupplierAgingPorCodigo (análisis de vencimiento) from the purchasing utils.

/** One proveedor's aging buckets at the cutoff (mirrors the on-screen table). */
export interface AgingRow {
  supplier_id: string;
  softland_code: string | null;
  nombre: string;
  porVencer: number;
  b1: number; // 1-30
  b2: number; // 31-60
  b3: number; // 61-90
  b4: number; // 90+
  total: number;
}

export interface AgingTotals {
  porVencer: number;
  b1: number;
  b2: number;
  b3: number;
  b4: number;
  total: number;
}

export interface CuentasPorPagarExportMeta {
  empresa: string;
  cutoff: string; // YYYY-MM-DD
  from?: string; // YYYY-MM-DD, start of emission range (optional)
  to?: string; // YYYY-MM-DD, end of emission range (optional)
  proveedor?: string; // nombre when a single supplier is selected
}

const HEADERS = [
  "Código",
  "Proveedor",
  "Por vencer",
  "1-30",
  "31-60",
  "61-90",
  "90+",
  "Total",
];

const COL_WIDTHS = [
  { wch: 12 }, // Código
  { wch: 44 }, // Proveedor
  { wch: 16 }, // Por vencer
  { wch: 16 }, // 1-30
  { wch: 16 }, // 31-60
  { wch: 16 }, // 61-90
  { wch: 16 }, // 90+
  { wch: 18 }, // Total
];

const fmtDate = (d?: string) => (d ? dayjs(d).format("DD/MM/YYYY") : "");

// Collapse anything that isn't a letter/number into "_" for a safe filename fragment.
const sanitize = (s: string) =>
  s
    .replace(/[^\p{L}\p{N}]+/gu, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

type Cell = string | number;

/** Resumen por proveedor — aging summary, one row per proveedor + a Totales row. */
export function exportCuentasPorPagarResumen(
  rows: AgingRow[],
  totals: AgingTotals,
  meta: CuentasPorPagarExportMeta
) {
  const aoa: Cell[][] = [];
  aoa.push(["Cuentas por Pagar — Antigüedad de saldos"]);
  aoa.push(["Empresa", meta.empresa]);
  aoa.push(["Fecha de corte", fmtDate(meta.cutoff)]);
  if (meta.from || meta.to) {
    aoa.push([
      "Rango de emisión",
      `${fmtDate(meta.from) || "…"} a ${fmtDate(meta.to) || "…"}`,
    ]);
  }
  if (meta.proveedor) aoa.push(["Proveedor", meta.proveedor]);
  aoa.push([]);
  aoa.push(HEADERS);

  for (const r of rows) {
    // Saldos kept numeric so Excel treats them as numbers.
    aoa.push([
      r.softland_code ?? "",
      r.nombre,
      r.porVencer,
      r.b1,
      r.b2,
      r.b3,
      r.b4,
      r.total,
    ]);
  }

  aoa.push([
    "",
    "Totales",
    totals.porVencer,
    totals.b1,
    totals.b2,
    totals.b3,
    totals.b4,
    totals.total,
  ]);

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = COL_WIDTHS;
  XLSX.utils.book_append_sheet(wb, ws, "Resumen");
  const suffix = meta.proveedor ? `_${sanitize(meta.proveedor)}` : "";
  XLSX.writeFile(wb, `Cuentas_Por_Pagar${suffix}_${meta.cutoff}.xlsx`);
}
